"use client";
import * as React from "react";
import Image from "next/image";
import Link from "next/link";
import { InstructorListInstanceProps } from "../types";
import useTranslations from "@/i18n/useTranslations";

const Panel = ({ instructors, title }: InstructorListInstanceProps) => {
  const instructorList = instructors;
  const tExpand = useTranslations("ExpandableBlock");
  const [isExpanded, setIsExpanded] = React.useState(false);

  const gridClass = "grid grid-cols-1 gap-4 lg:grid-cols-2";

  const getCardVisibilityClass = (index: number) => {
    if (isExpanded) return "";
    if (index >= 4) return "hidden";
    if (index >= 2) return "hidden md:flex";
    return "";
  };

  const showExpandTrigger = instructorList.length > 2;
  const expandTriggerClass = `
    flex justify-center mt-5
    ${instructorList.length <= 2 ? "hidden" : ""}
    ${instructorList.length > 4 ? "md:flex" : "md:hidden"}
  `;

  return (
    <div>
      {title ? (
        <div className="flex justify-start align-center mb-1">
          <h2 className="md:text-3xl text-primary mt-0">{title}</h2>
        </div>
      ) : null}
      <div className={gridClass}>
        {instructorList.map((card, index) => (
          <Link
            key={card.id}
            href={`/bio/${card.slug}`}
            className={`
              flex flex-col justify-between gap-4 rounded-xl border border-putty-400 bg-white p-4 sm:flex-row sm:p-8
              ${getCardVisibilityClass(index)}
            `}
          >
            <div className="flex flex-row gap-4">
              {card.imageUrl && (
                <div className="hidden h-[90px] w-[90px] flex-shrink-0 items-center justify-center sm:flex">
                  <Image
                    src={card.imageUrl}
                    alt={card.firstName ?? ""}
                    title={card.firstName ?? undefined}
                    width={110}
                    height={110}
                    className="h-[90px] w-[90px] rounded-full object-cover"
                  />
                </div>
              )}
              <div className="flex flex-col justify-center gap-2">
                <p className="m-0 text-base font-bold text-secondary-500">
                  {card.firstName} {card.lastName}
                </p>
                <p className="m-0 font-bold text-base text-gray-800">
                  {card.position ?? ""}
                  {card.position && card.organizationName && " at "}
                  {card.organizationName ?? ""}
                </p>
              </div>
            </div>

            {card.partners?.[0]?.logoUrl && (
              <div className="flex items-center justify-center self-start">
                <Image
                  src={card.partners[0].logoUrl}
                  alt={card.partners[0].name}
                  width={162}
                  height={96}
                  className="h-auto max-h-[96px] w-[116px] max-w-[116px] rounded-full sm:w-[162px] sm:max-w-[162px]"
                />
              </div>
            )}
          </Link>
        ))}
      </div>
      {showExpandTrigger && (
        <div className={expandTriggerClass}>
          <span
            className="underline cursor-pointer"
            onClick={() => setIsExpanded((prev) => !prev)}
          >
            {!isExpanded ? tExpand("showMore") : tExpand("showLess")}
          </span>
        </div>
      )}
    </div>
  );
};

export default Panel;
